"use client";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { FlaskConical, Calculator, GraduationCap } from "lucide-react";
export default function AcademicTutoring() {
  const {
    ref: card1Ref,
    isVisible: card1Visible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: card2Ref,
    isVisible: card2Visible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: card3Ref,
    isVisible: card3Visible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section className="relative py-24 overflow-hidden bg-light-background">
      {}
      <div className="absolute inset-0 bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1503393/img/study_desk_softly_lit_background.png)] opacity-30" />
      
      {}
      <div className="absolute top-20 left-10 w-16 h-16 opacity-10">
        <svg viewBox="0 0 100 100" className="fill-accent">
          <path d="M50 10 Q30 30 50 50 Q70 30 50 10 M50 50 L50 90" stroke="currentColor" strokeWidth="3" fill="none" />
        </svg>
      </div>
      <div className="absolute bottom-32 right-16 w-20 h-20 opacity-10">
        <svg viewBox="0 0 100 100" className="fill-accent">
          <path d="M50 10 Q30 30 50 50 Q70 30 50 10 M50 50 L50 90" stroke="currentColor" strokeWidth="3" fill="none" />
        </svg>
      </div>

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-display font-bold text-primary mb-4">
            Academic Tutoring
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Expert online tutoring to help students excel in core subjects with personalised learning plans and confidence-building support.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative z-10">
          {}
          <div ref={card1Ref} className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] ${card1Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Card className="h-full hover:shadow-strong hover:-translate-y-0.5 transition-all duration-[200ms] ease-out bg-white border border-border">
              <CardHeader className="pb-4">
                <div className="w-14 h-14 rounded-full bg-primary-light flex items-center justify-center mb-4">
                  <FlaskConical className="w-7 h-7 text-primary" />
                </div>
                <CardTitle className="text-2xl text-primary">Science Tutoring</CardTitle>
                <CardDescription className="text-base">KS3, GCSE and A-Level Science tutoring covering Biology, Chemistry, and Physics with expert UK-qualified tutors.</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground mb-6">
                  <li className="flex items-start">
                    <span className="text-secondary mr-2">•</span>
                    <span>All science disciplines at all levels</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-secondary mr-2">•</span>
                    <span>Exam techniques and practical skills</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-secondary mr-2">•</span>
                    <span>Personalised study plans</span>
                  </li>
                </ul>
                <Link to="/ProgrammesPage" className="inline-flex items-center justify-center rounded-md bg-primary text-foreground-primary px-6 py-3 font-semibold hover:bg-primary-hover hover:shadow-medium hover:-translate-y-0.5 active:bg-primary-active active:translate-y-0 focus-visible:outline-none transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] w-full">
                  Explore Programmes
                </Link>
              </CardContent>
            </Card>
          </div>

          {}
          <div ref={card2Ref} className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] delay-100 ${card2Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Card className="h-full hover:shadow-strong hover:-translate-y-0.5 transition-all duration-[200ms] ease-out bg-white border border-border">
              <CardHeader className="pb-4">
                <div className="w-14 h-14 rounded-full bg-secondary-light flex items-center justify-center mb-4">
                  <Calculator className="w-7 h-7 text-secondary" />
                </div>
                <CardTitle className="text-2xl text-primary">Maths Tutoring</CardTitle>
                <CardDescription className="text-base">
                  Comprehensive Maths tutoring up to GCSE level, building confidence and problem-solving skills.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground mb-6">
                  <li className="flex items-start">
                    <span className="text-secondary mr-2">•</span>
                    <span>KS3 and GCSE Maths coverage</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-secondary mr-2">•</span>
                    <span>Focus on core concepts and exam strategies</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-secondary mr-2">•</span>
                    <span>Regular progress tracking</span>
                  </li>
                </ul>
                <Link to="/ProgrammesPage" className="inline-flex items-center justify-center rounded-md bg-primary text-foreground-primary px-6 py-3 font-semibold hover:bg-primary-hover hover:shadow-medium hover:-translate-y-0.5 active:bg-primary-active active:translate-y-0 focus-visible:outline-none transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] w-full">
                  Explore Programmes
                </Link>
              </CardContent>
            </Card>
          </div>

          {}
          <div ref={card3Ref} className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] delay-200 ${card3Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Card className="h-full hover:shadow-strong hover:-translate-y-0.5 transition-all duration-[200ms] ease-out bg-white border border-border">
              <CardHeader className="pb-4">
                <div className="w-14 h-14 rounded-full bg-accent-light flex items-center justify-center mb-4">
                  <GraduationCap className="w-7 h-7 text-accent" />
                </div>
                <CardTitle className="text-2xl text-primary">11+ Exam Prep</CardTitle>
                <CardDescription className="text-base">
                  Specialist preparation for 11+ entrance exams, covering reasoning, English, and Maths.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground mb-6">
                  <li className="flex items-start">
                    <span className="text-secondary mr-2">•</span>
                    <span>Verbal and non-verbal reasoning</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-secondary mr-2">•</span>
                    <span>Exam technique and confidence building</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-secondary mr-2">•</span>
                    <span>Tailored mock exams and feedback</span>
                  </li>
                </ul>
                <Link to="/ProgrammesPage" className="inline-flex items-center justify-center rounded-md bg-primary text-foreground-primary px-6 py-3 font-semibold hover:bg-primary-hover hover:shadow-medium hover:-translate-y-0.5 active:bg-primary-active active:translate-y-0 focus-visible:outline-none transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] w-full">
                  Explore Programmes
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}